import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { formatBillingPrice } from '../../../lib/utils/billingPlans';
import { getPlanName } from '../../../lib/utils/billingLabels';
import { previewPlanChange, getCandidatesCount } from '../../../lib/api';
import {
  getCandidatesOverLimitCount,
  getPlanMaxCandidates,
} from '../../../lib/utils/planLimits';
import { useAuth } from '../../../contexts/AuthContext';

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PlanChangeModal = ({ open, target, onClose, onConfirm, confirming = false, error = '' }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [overLimitCount, setOverLimitCount] = useState(0);

  useEffect(() => {
    if (!open || !target?.price?.priceId) return;

    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setPreviewError('');
    setOverLimitCount(0);

    const targetMax = getPlanMaxCandidates(target.product.id);

    Promise.all([
      previewPlanChange({
        productId: target.product.id,
        priceId: target.price.priceId,
        interval: target.interval,
      }),
      user?.id ? getCandidatesCount(user.id) : Promise.resolve(0),
    ])
      .then(([data, count]) => {
        if (cancelled) return;
        setPreview(data);
        setOverLimitCount(getCandidatesOverLimitCount(count, targetMax));
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(err?.message || t('billing.previewError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, target, t, user?.id]);

  if (!open || !target) return null;

  const isUpgrade = preview?.direction === 'upgrade';
  const isDowngrade = preview?.direction === 'downgrade';
  const chargeAmount = preview?.amountDue
    ? formatBillingPrice({ amount: preview.amountDue, currency: preview.currency })
    : null;
  const effectiveDate = formatDate(preview?.effectiveAt);
  const productName = getPlanName(target.product.id);
  const intervalLabel = target.interval === 'year' ? t('billing.yearly') : t('billing.monthly');
  const showOverLimitWarning = isDowngrade && overLimitCount > 0;

  const handleConfirm = () => {
    onConfirm?.({ ...target, direction: preview?.direction });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-elevation-lg">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {t('billing.switchToName', { name: productName })}
            {target.interval ? ` (${intervalLabel})` : ''}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t('common.close')}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
            {t('billing.calculatingChange')}
          </div>
        ) : previewError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-4">
            {previewError}
          </div>
        ) : (
          <div className="space-y-3 mb-5 text-sm">
            {isUpgrade && (
              <p className="text-muted-foreground leading-relaxed">
                {t('billing.upgradeCharge', {
                  amount: chargeAmount ?? t('billing.proratedDifference'),
                })}
              </p>
            )}

            {isDowngrade && (
              <p className="text-muted-foreground leading-relaxed">
                {t('billing.downgradeKeep', {
                  date: effectiveDate ?? t('billing.endOfPeriod'),
                })}
              </p>
            )}

            {showOverLimitWarning && (
              <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-foreground">
                <p className="font-medium mb-1">{t('billing.overLimitWarningTitle')}</p>
                <p className="text-muted-foreground leading-relaxed">
                  {t('billing.overLimitWarningBody', {
                    count: overLimitCount,
                    date: effectiveDate ?? t('billing.endOfPeriod'),
                    limit: getPlanMaxCandidates(target.product.id),
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-4">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={onClose} disabled={confirming}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            loading={confirming}
            disabled={loading || Boolean(previewError) || !preview}
          >
            {isUpgrade ? t('billing.confirmAndPay') : t('billing.confirmChange')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlanChangeModal;
